import Rule from '@/common/components/rule';
import Spacer from '@/common/components/spacer';
import { cn } from '@/shadcn-ui/utils';
import SavingsLineItemDescriptionWithToolTip from './savings-line-item-description-with-tooltip';
import DayOneLossCard, { DayOneLossCardProps } from './day-one-loss-card';
import SavingsReceiptSubLineItem from './savings-receipt-sub-line-item';
import SavingsReceiptTitleWithTooltip from './savings-receipt-title-with-tooltip';

export interface SavingsLineItem {
  description: string;
  subDescription?: string;
  lineItemTooltipTitle?: string;
  lineItemTooltipDescription?: string;
  subLineItemTooltipDescription?: string;
  price: string;
  isStrong?: boolean;
  /** If true, treat the line item as a secondary subtotal and add a horizontal rule above */
  isPseudoSubTotal?: boolean;
  subLineItems?: SavingsLineItem[];
  hasSalesTaxApplied?: boolean;
  suppressPriceDisplay?: boolean;
  bottomSpacerHeight?: number;
}

interface SavingsReceiptProps extends Partial<DayOneLossCardProps> {
  totalTitle: string;
  tooltipTitle?: string;
  tooltipDescription?: string;
  lineItems: SavingsLineItem[];
  subTotalLineItems?: SavingsLineItem[];
  totalLineItem?: SavingsLineItem;
  className?: string;
}

export default function SavingsReceipt({
  totalTitle,
  tooltipTitle,
  tooltipDescription,
  lineItems,
  subTotalLineItems,
  totalLineItem,
  totalPrice,
  wholesalePrice,
  dayOneLossAmount,
  className,
}: SavingsReceiptProps) {
  return (
    <div className={cn('flex flex-col px-[16px] py-[24px]', className)}>
      {totalTitle.length > 0 ? (
        <>
          {tooltipDescription ? (
            <SavingsReceiptTitleWithTooltip
              title={totalTitle}
              tooltipTitle={tooltipTitle}
              tooltipDescription={tooltipDescription}
            />
          ) : (
            <p className="body-strong">{totalTitle}</p>
          )}
          <Spacer variant="height16" />
        </>
      ) : null}
      {lineItems.map((l, index) => {
        return (
          <>
            {l.isPseudoSubTotal ? (
              <>
                <div className="flex flex-row justify-end">
                  <Rule direction="horizontal" className="w-[80px]" />
                </div>
                <Spacer variant="height8" />
              </>
            ) : null}

            <div key={l.description} className="flex flex-col">
              <div className="flex flex-row justify-between">
                {l.lineItemTooltipDescription ? (
                  <SavingsLineItemDescriptionWithToolTip
                    title={
                      l.lineItemTooltipTitle ? l.lineItemTooltipTitle : l.description
                    }
                    description={l.description}
                    tooltipDescription={l.lineItemTooltipDescription}
                    isTriggerStrong={l.isStrong}
                  />
                ) : (
                  <p
                    className={cn({
                      'body2': !l.isStrong,
                      'body2-strong': l.isStrong,
                    })}
                  >
                    {l.description}:
                  </p>
                )}

                {!l.suppressPriceDisplay && (
                  <p
                    className={cn({
                      'body2': !l.isStrong,
                      'body2-strong': l.isStrong,
                    })}
                  >
                    {l.price}
                  </p>
                )}
              </div>

              {l.subLineItems?.map((sub) => {
                return <SavingsReceiptSubLineItem lineItem={sub} key={sub.description} />;
              })}
            </div>
            {index < lineItems.length - 1 ? (
              <Spacer variant={l.bottomSpacerHeight === 8 ? 'height8' : 'height16'} />
            ) : null}
          </>
        );
      })}

      {subTotalLineItems?.map((st, index) => {
        return (
          <>
            {index === 0 ? (
              <>
                <Spacer variant="height8" />
                <div className="flex flex-row justify-end">
                  <div className="w-[80px]">
                    <Rule direction="horizontal" />
                  </div>
                </div>
              </>
            ) : null}

            <Spacer variant="height8" />
            <div className="flex flex-row justify-between">
              <p
                className={cn({
                  'body2': !st.isStrong,
                  'body2-strong': st.isStrong,
                })}
              >
                {st.description}:
              </p>
              <p
                className={cn({
                  'body2': !st.isStrong,
                  'body2-strong': st.isStrong,
                })}
              >
                {st.price}
              </p>
            </div>
            {st.subLineItems?.map((sub) => {
              return <SavingsReceiptSubLineItem lineItem={sub} key={sub.description} />;
            })}
          </>
        );
      })}

      {totalLineItem ? (
        <>
          <Spacer variant="height8" />
          <div className="flex flex-row justify-end">
            <div className="w-[80px]">
              <Rule direction="horizontal" />
            </div>
          </div>
          <Spacer variant="height8" />
          <div className="flex flex-row justify-between">
            <p className="body2-strong">{totalLineItem.description}:</p>
            <p className="body2-strong">{totalLineItem.price}</p>
          </div>
          {totalLineItem.hasSalesTaxApplied ? (
            <>
              <Spacer variant="height8" />
              <div className="flex flex-row justify-end">
                <p className="body3 text-flexcar-neutral50">Includes sales tax</p>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {totalPrice !== undefined &&
      wholesalePrice !== undefined &&
      dayOneLossAmount !== undefined ? (
        <>
          <Spacer variant="height8" />
          <DayOneLossCard
            totalPrice={totalPrice}
            wholesalePrice={wholesalePrice}
            dayOneLossAmount={dayOneLossAmount}
          />
        </>
      ) : null}
    </div>
  );
}
